/**
 * Resolves an old, org-less link (`/trials/...`) to the organization that has
 * the page. Kept free of React and Firebase: the caller passes the page's
 * document chain (from `orgDocChainForSubpath`) and a probe that reads one
 * document in one org.
 *
 * An org has the page only when every document in the chain exists. Copy
 * scripts reuse trial and experiment IDs across orgs, and deleting an
 * experiment leaves its tasks behind, so the deepest document alone could let
 * an orphaned task beat a real copy elsewhere.
 */

export type DocProbeResult = "found" | "missing" | "denied" | "error";

/** Reads one document, given its path relative to `organizations/{orgId}/`. */
export type DocProbe = (orgId: string, relativeDocPath: string) => Promise<DocProbeResult>;

/** `miss`: some document is missing or unreadable. `unknown`: nothing is missing but a read failed. */
export type DocChainCheck = "match" | "miss" | "unknown";

export type LegacyLinkResolution =
  | { kind: "redirect"; orgId: string }
  | { kind: "choose"; orgIds: string[] }
  | { kind: "notFound" }
  | { kind: "error" };

/**
 * How many orgs are probed at once. Super admins search every org, so about
 * 25 in flight keeps even that to a few round trips.
 */
export const LEGACY_LINK_PROBE_CONCURRENCY = 25;

/**
 * Whether every document in `docChain` exists. Probes the deepest document
 * first and, only if it is found, its ancestors in parallel. Denied counts as
 * missing; an error or a rejected probe makes the result unknown unless some
 * document is definitely missing.
 */
export async function checkDocChain(
  docChain: readonly string[],
  probe: (relativeDocPath: string) => Promise<DocProbeResult>
): Promise<DocChainCheck> {
  const read = async (path: string): Promise<DocProbeResult> => {
    try {
      return await probe(path);
    } catch {
      return "error";
    }
  };
  const classify = (results: DocProbeResult[]): DocChainCheck => {
    if (results.some((r) => r === "missing" || r === "denied")) return "miss";
    if (results.some((r) => r !== "found")) return "unknown";
    return "match";
  };

  if (docChain.length === 0) {
    return "match";
  }
  const deepest = await read(docChain[docChain.length - 1]);
  if (deepest !== "found") {
    return classify([deepest]);
  }
  return classify(await Promise.all(docChain.slice(0, -1).map(read)));
}

/**
 * Finds the org(s) holding the page whose documents are `docChain`. The
 * current org is checked first and wins outright on a match; otherwise the
 * remaining candidates are probed, `LEGACY_LINK_PROBE_CONCURRENCY` orgs at a
 * time. One match redirects, several offer a choice (in candidate order),
 * none is not found, or an error when some org could not be checked.
 *
 * Once `signal` aborts no further org is probed, and the caller should ignore
 * the result. An empty `docChain` (a section page, valid in any org) is a
 * caller error and rejects.
 */
export async function resolveLegacyLink({
  docChain,
  currentOrgId,
  candidateOrgIds,
  probe,
  signal,
}: {
  docChain: readonly string[];
  currentOrgId: string | null | undefined;
  candidateOrgIds: readonly string[];
  probe: DocProbe;
  signal?: AbortSignal;
}): Promise<LegacyLinkResolution> {
  if (docChain.length === 0) {
    throw new Error("resolveLegacyLink needs a document chain; a section page is valid in any org");
  }
  const checkOrg = (orgId: string) => checkDocChain(docChain, (path) => probe(orgId, path));

  const current = currentOrgId ? await checkOrg(currentOrgId) : null;
  if (current === "match") {
    return { kind: "redirect", orgId: currentOrgId };
  }

  const others = [...new Set(candidateOrgIds)].filter((orgId) => orgId && orgId !== currentOrgId);
  const checks: DocChainCheck[] = [];
  let next = 0;
  const worker = async () => {
    while (next < others.length && !signal?.aborted) {
      const index = next++;
      checks[index] = await checkOrg(others[index]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(LEGACY_LINK_PROBE_CONCURRENCY, others.length) }, worker));

  const matches = others.filter((_, index) => checks[index] === "match");
  if (matches.length === 1) {
    return { kind: "redirect", orgId: matches[0] };
  }
  if (matches.length > 1) {
    return { kind: "choose", orgIds: matches };
  }
  return current === "unknown" || checks.includes("unknown") ? { kind: "error" } : { kind: "notFound" };
}
